import type { ReactNode } from "react";
import { Dialog as RadixDialog } from "radix-ui";
import { AnimatePresence, motion } from "motion/react";
import { cn } from "./cn";
import { softSpring } from "./motion";

export type DialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: ReactNode;
  description?: ReactNode;
  className?: string;
  children?: ReactNode;
};

/** A small centered modal for a decision that needs the user's full attention, such as confirming something that can't be undone. */
export function Dialog({ open, onOpenChange, title, description, className, children }: DialogProps) {
  return (
    <RadixDialog.Root open={open} onOpenChange={onOpenChange}>
      <AnimatePresence>
        {open ? (
          <RadixDialog.Portal forceMount>
            <RadixDialog.Overlay asChild forceMount>
              <motion.div
                className="fixed inset-0 z-50 bg-accent/30 backdrop-blur-[2px]"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2 }}
              />
            </RadixDialog.Overlay>
            <div className="pointer-events-none fixed inset-0 z-50 grid place-items-center p-4">
              <RadixDialog.Content asChild forceMount {...(description ? {} : { "aria-describedby": undefined })}>
                <motion.div
                  className={cn("pointer-events-auto w-full max-w-[440px] rounded-xl bg-surface p-6 text-ink shadow-raised outline-none", className)}
                  initial={{ opacity: 0, y: 12, scale: 0.97 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: 8, scale: 0.98 }}
                  transition={softSpring}
                >
                  <RadixDialog.Title className="font-display text-[22px] leading-tight text-balance">{title}</RadixDialog.Title>
                  {description ? <RadixDialog.Description className="mt-2 text-sm leading-relaxed text-ink-soft">{description}</RadixDialog.Description> : null}
                  {children}
                </motion.div>
              </RadixDialog.Content>
            </div>
          </RadixDialog.Portal>
        ) : null}
      </AnimatePresence>
    </RadixDialog.Root>
  );
}
